import React, { useMemo } from 'react';

import { Form } from 'antd';

import { getCommunityContractLabel } from 'components/CMASummary/utils';
import { sortCommunityContracts } from 'components/CommunityInfo/careTypes/utils';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { CommunityContract } from 'types/contracts';

import { useCMASummaryContractsFilterContext } from './CMASummaryContractsFilterContext';
import CMASummaryFilterContractSelectionSelect from './CMASummaryFilterContractSelectionSelect';
import { CONTRACT_DISPLAY_TYPES } from '../../../../constants';

type Props = {
  maxSelectable?: number;
  forceDisableSelectAll?: boolean;
  hideSelectAll?: boolean;
};

const CMASummaryFilterContractSelection = ({
  maxSelectable,
  forceDisableSelectAll = false,
  hideSelectAll = false,
}: Props) => {
  const { community, competitors } = useCommunityViewContext();
  const { form } = useCMASummaryContractsFilterContext();

  const selectedContractSlugs = Form.useWatch('communityContractSlugs', form) ?? [];

  const contractDisplayType = Form.useWatch('contractDisplayType', form) ?? form?.getFieldValue('contractDisplayType');
  const contractSelectionType =
    Form.useWatch('contractSelectionType', form) ?? form?.getFieldValue('contractSelectionType');

  const options = useMemo(() => {
    let contracts = [];
    if (contractDisplayType === CONTRACT_DISPLAY_TYPES.EVERYONES_CONTRACTS) {
      contracts = [...(community?.contracts || []), ...(competitors?.flatMap((c) => c.contracts) || [])];
    } else if (contractDisplayType === CONTRACT_DISPLAY_TYPES.MY_CONTRACTS) {
      contracts = community?.contracts || [];
    }

    const uniqueContracts: CommunityContract[] = Array.from(
      new Map<string, CommunityContract>(contracts.map((c: CommunityContract) => [c.slug, c])).values()
    );

    return (
      uniqueContracts.sort(sortCommunityContracts).map((c: CommunityContract) => ({
        label: getCommunityContractLabel(c),
        value: c.slug,
      })) ?? []
    );
  }, [community, competitors, contractDisplayType, contractSelectionType]);

  const setSelectedItems = (items: any[]) => {
    const values = items.map((item) => item.value);
    form?.setFieldsValue({ communityContractSlugs: values });
  };

  const selectedItems = options.filter((opt: any) => selectedContractSlugs.includes(opt.value));
  const nonSelectedItems = options.filter((opt: any) => !selectedContractSlugs.includes(opt.value));

  return (
    <CMASummaryFilterContractSelectionSelect
      open
      options={options}
      selectedItems={selectedItems}
      nonSelectedItems={nonSelectedItems}
      setSelectedItems={setSelectedItems}
      maxSelectable={maxSelectable}
      forceDisableSelectAll={forceDisableSelectAll}
      hideSelectAll={hideSelectAll}
    />
  );
};

export default CMASummaryFilterContractSelection;
